import { useState } from 'react'
import { useParams } from 'react-router'

import MoreHorizontalIcon from '@icons/MoreHorizontalIcon'

import MenuMui from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'

import useDialog from '@shared/hooks/src/useDialog'
import { RoleGuard } from '@shared/providers/src/MeProvider'
import { usePatient } from '@shared/providers/src/PatientProvider'
import { handleError, UserRole } from '@shared/utils'

import ConfirmationV2 from '@components/Dialog/ConfirmationV2'
import { usePatientAccessible } from '@components/PatientAccessGuard'

import { IconCircleButton } from '../../ProfileTab.utils'
import GileadApplication from '../GileadApplication'
import { UpdateType, useUserStatusUpdate } from '../Status.hooks'
import ChangeStatusDialog from './ChangeStatusDialog'

function Menu() {
  const [anchorEl, setAnchorEl] = useState(null)
  const [reasonDialog, setReasonDialog] = useState(null)

  const open = Boolean(anchorEl)
  const isReasonDialogOpen = Boolean(reasonDialog)

  const patient = usePatient()

  const enrolled = patient?.enrolled
  const active = patient?.active
  const disabled = patient?.disabled

  const updateUserStatus = useUserStatusUpdate()

  const unlockConfirm = useDialog({
    component: ConfirmationV2,
    props: ({ close }) => ({
      maxWidth: 'xs',
      variant: 'warning',
      title: 'Unlock Account',
      description: "Unlock this patient's account? They will be able to log in again.",
      rejectLabel: 'Cancel',
      confirmLabel: 'Unlock',
      onReject: () => close(),
      onConfirm: () => updateUserStatus.mutateAsync({ type: UpdateType.Unlock }).then(close).catch(handleError),
    }),
  })

  const handleMenuOpen = (event) => setAnchorEl(event?.currentTarget)
  const handleMenuClose = () => setAnchorEl(null)

  const handleMakeActive = () => {
    updateUserStatus.mutateAsync({ type: UpdateType.Activate }).catch(handleError)
  }

  const handleConfirm = ({ reason, details }) => {
    return updateUserStatus
      .mutateAsync({
        type: reasonDialog?.status === 'archived' ? UpdateType.Deactivate : UpdateType.Disable,
        data: { reason, reason_detail: details },
      })
      .then(() => setReasonDialog(null))
      .catch(handleError)
  }

  return (
    <>
      <IconCircleButton
        id="status-menu-button"
        aria-controls={open ? 'status-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleMenuOpen}
        disabled={!patient}
        size="small"
        color="inherit"
      >
        <MoreHorizontalIcon />
      </IconCircleButton>
      <MenuMui
        id="status-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          list: { 'aria-labelledby': 'status-menu-button' },
        }}
      >
        <RoleGuard prohibited={[UserRole.Pharmacy, UserRole.PharmacyManager]}>
          {enrolled && (
            <MenuItem
              onClick={() => {
                handleMenuClose()
                !active
                  ? handleMakeActive()
                  : setReasonDialog({
                      title: active ? 'Set Inactive?' : 'Set Active?',
                      status: active ? 'archived' : 'active',
                    })
              }}
            >
              Set {!active ? 'Active' : 'Inactive'}
            </MenuItem>
          )}
        </RoleGuard>
        <RoleGuard prohibited={[UserRole.Provider, UserRole.Pharmacy, UserRole.PharmacyManager]}>
          <MenuItem
            onClick={() => {
              handleMenuClose()
              disabled
                ? handleMakeActive()
                : setReasonDialog({
                    title: disabled ? 'Set Enabled?' : 'Set Disabled?',
                    status: disabled ? 'active' : 'deleted',
                  })
            }}
            sx={disabled ? undefined : { color: 'error.main' }}
          >
            {disabled ? 'Enable' : 'Disable'}
          </MenuItem>
        </RoleGuard>
        <RoleGuard allowed={[UserRole.Admin, UserRole.Support]}>
          <MenuItem
            onClick={() => {
              handleMenuClose()
              unlockConfirm()
            }}
          >
            Unlock
          </MenuItem>
        </RoleGuard>
        <GileadApplication />
      </MenuMui>
      <ChangeStatusDialog
        patientId={patient?.id}
        open={isReasonDialogOpen}
        title={reasonDialog?.title}
        onClose={() => setReasonDialog(null)}
        onConfirm={handleConfirm}
      />
    </>
  )
}

export default function Wrap() {
  const { id } = useParams()
  const patient = usePatient()
  const patientAccessible = usePatientAccessible(id)

  return (
    <RoleGuard
      predicate={(me) => {
        if (me.role === UserRole.Manager || me.role === UserRole.Manager340B) return false
        if (me.role === UserRole.Compliance) return false
        if (me.role === UserRole.ClinicalLHC || me.role === UserRole.ClinicalQI) return false
        if (me.role === UserRole.Provider && !patient?.enrolled) return false
        if ((me.role === UserRole.Pharmacy || me.role === UserRole.PharmacyManager) && patient?.pendingGileadApplications) return false
        return patientAccessible
      }}
    >
      <Menu />
    </RoleGuard>
  )
}
